import { ReactNode } from 'react';
import { IconLookup } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { cn } from '@v2/shared/lib/utils';

type Props = {
  icon?: IconLookup;
  color?: 'primary' | 'danger' | 'info' | 'secondary';
  size?: 'sm' | 'base' | 'lg';
  className?: string;
  disabled?: boolean;
  iconAfter?: IconLookup;
  onClick?: () => void;
  children: ReactNode;
};

/**
 * A button that reads as a link.
 *
 * Off headless_jshop. Button is the other one, and the difference is what the control
 * does rather than where it sits: Button is the page's action, this is a secondary one
 * beside it — opening a dialog, stepping back through a flow. `type="button"` because
 * several of these sit inside forms and would otherwise submit them.
 *
 * Not `ButtonLink`, which is the mirror image of this one and easy to reach for by
 * mistake: that is an `<a>` dressed as a button, this is a `<button>` dressed as a link.
 * The head noun of each name is the element, so a control that navigates is one of the
 * two Links and a control that acts is one of the two Buttons — see the grid in
 * `ButtonLink`. Nothing here navigates: there is no `href`, only an `onClick`.
 */
export default function TextButton({
  icon,
  color = 'info',
  size = 'base',
  iconAfter,
  disabled,
  className,
  onClick,
  children,
}: Props) {
  const colorClasses = {
    // A name rather than a shade, as Button's is: it resolves to --v2-primary, so the
    // same button is each brand's own colour without the caller knowing which.
    primary: 'text-primary hover:text-primary-700 active:text-primary-800',
    danger: 'text-red-600 hover:text-red-700 active:text-red-800',
    info: 'text-sky-600 hover:text-sky-700 active:text-sky-800',
    secondary: 'text-slate-600 hover:text-slate-700 active:text-slate-800',
  };

  const sizeClasses = {
    sm: 'text-sm',
    base: 'text-base',
    lg: 'text-lg',
  };

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        colorClasses[color],
        sizeClasses[size],
        'bg-transparent border-0 p-0 cursor-pointer hover:underline font-semibold',
        disabled && 'opacity-50 cursor-not-allowed',
        className
      )}
    >
      {icon && <FontAwesomeIcon icon={icon} className="mr-1.5" />}
      <span>{children}</span>
      {iconAfter && <FontAwesomeIcon icon={iconAfter} className="ml-1" />}
    </button>
  );
}
